import React, { useState } from 'react';
import { Button, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BoardDetailMenu } from '@/components/Board/BoardDetail/styles';
import LanguageIcon from '@/public/icons/record/language.svg';
import { Language, SelectableLanguages } from '@/types/api';
import type { AppLanguageCode } from '@/utils/language';
import useRecordTranslationView from '@/components/Record/TalkFLow/hooks/useRecordTranslationView';

/**
 * 제목줄의 번역 언어 선택 버튼.
 *
 * 문서 전체(제목/AI 요약/AI 정리/메모)를 한 번에 번역하는 단일 진입점이다.
 * 원문 복귀는 상단 띠(TranslationStatusBar)의 취소가 담당하므로 여기엔 두지 않는다.
 *
 * 지원 언어는 `SelectableLanguages`(ko/en/zh) — 번역 API 의 targetLanguage 계약이
 * ko/en/zh-CN 로 닫혀 있어 그 이상은 넓히지 않는다.
 */
type Props = {
  docId: string;
  disabled?: boolean;
  onSelect: (language: AppLanguageCode) => void;
};

const TranslationLanguageButton = ({ docId, disabled, onSelect }: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const { isActive, isTranslating, targetLanguage } =
    useRecordTranslationView(docId);

  const activeLanguage = isActive ? targetLanguage : undefined;

  const handleSelect = (language: AppLanguageCode) => {
    setAnchorEl(null);
    onSelect(language);
  };

  return (
    <>
      <Button
        id={'translation-button'}
        aria-controls={isOpen ? 'translation-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        aria-label={t('번역하기')}
        disabled={disabled || isTranslating}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(event.currentTarget)
        }
        sx={{
          padding: '3px',
          minWidth: '24px',
          '&:hover': { backgroundColor: 'var(--dk-bg-muted, #F3F5F6)' },
        }}
      >
        <LanguageIcon
          style={{ color: activeLanguage ? '#C77AC4' : '#888D96' }}
        />
      </Button>
      <BoardDetailMenu
        id={'translation-menu'}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': 'translation-button' }}
      >
        {SelectableLanguages.map((language) => (
          <MenuItem
            key={language}
            selected={language === activeLanguage}
            onClick={() => handleSelect(language)}
          >
            {Language[language]}
          </MenuItem>
        ))}
      </BoardDetailMenu>
    </>
  );
};

export default TranslationLanguageButton;
